import React, { useState, useEffect } from 'react';
import { AppLayout } from '@/layouts/AppLayout';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Table } from '@/components/ui/Table';
import { Pagination } from '@/components/ui/Pagination';
import { api } from '@/lib/api';
import type { License, QueryFilters, TableColumn } from '@/types';
import {
  MagnifyingGlassIcon,
  FunnelIcon,
  ArrowDownTrayIcon,
  BookmarkIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { StarIcon } from '@heroicons/react/20/solid';

const LICENSE_TYPES = [
  { value: 'ON-SALE GENERAL', label: 'On-Sale General' },
  { value: 'OFF-SALE GENERAL', label: 'Off-Sale General' },
  { value: 'ON-SALE BEER AND WINE', label: 'On-Sale Beer and Wine' },
  { value: 'OFF-SALE BEER AND WINE', label: 'Off-Sale Beer and Wine' },
  { value: 'MANUFACTURER', label: 'Manufacturer' },
  { value: 'WHOLESALER', label: 'Wholesaler' },
];

const LICENSE_STATUS = [
  { value: 'ACTIVE', label: 'Active' },
  { value: 'INACTIVE', label: 'Inactive' },
  { value: 'SUSPENDED', label: 'Suspended' },
  { value: 'REVOKED', label: 'Revoked' },
];



export const SearchPage: React.FC = () => {
  const [filters, setFilters] = useState<QueryFilters>({
    limit: 25,
    offset: 0,
  });
  const [results, setResults] = useState<License[]>([]);
  const [loading, setLoading] = useState(false);
  const [totalResults, setTotalResults] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [showFilters, setShowFilters] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const columns: TableColumn<License>[] = [
    {
      key: 'business_name',
      label: 'Business Name',
      render: (value, item) => (
        <div>
          <div className="font-medium text-gray-900">{value}</div>
          {item.dba_name && item.dba_name !== value && (
            <div className="text-sm text-gray-500">DBA: {item.dba_name}</div>
          )}
        </div>
      ),
    },
    {
      key: 'license_type',
      label: 'License Type',
      render: (value) => (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
          {value}
        </span>
      ),
    },
    {
      key: 'premise_city',
      label: 'Location',
      render: (value, item) => (
        <div>
          <div className="text-sm text-gray-900">{value}</div>
          <div className="text-sm text-gray-500">{item.premise_county}</div>
        </div>
      ),
    },
    {
      key: 'google_rating',
      label: 'Rating',
      render: (value, item) => {
        if (!value) return <span className="text-gray-400">-</span>;
        return (
          <div className="flex items-center">
            <StarIcon className="h-4 w-4 text-yellow-400" />
            <span className="ml-1 text-sm text-gray-900">{value}</span>
            {item.google_user_ratings_total && (
              <span className="ml-1 text-xs text-gray-500">
                ({item.google_user_ratings_total})
              </span>
            )}
          </div>
        );
      },
    },
    {
      key: 'license_number',
      label: 'License #',
      render: (value) => (
        <span className="font-mono text-sm text-gray-600">{value}</span>
      ),
    },
  ];

  const handleSearch = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await api.queryData(filters);
      setResults(response.licenses);
      setTotalResults(response.total);
    } catch (err) {
      setError('An error occurred while searching');
      console.error('Search error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    setFilters(prev => ({
      ...prev,
      offset: (page - 1) * (prev.limit || 25),
    }));
  };

  const handleFilterChange = (key: keyof QueryFilters, value: any) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
    }));
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setFilters({
      limit: 25,
      offset: 0,
    });
    setCurrentPage(1);
    setResults([]);
    setTotalResults(0);
  };

  const exportResults = async () => {
    // TODO: Implement CSV export
    console.log('Export functionality to be implemented');
  };

  const saveQuery = async () => {
    // TODO: Implement save query
    console.log('Save query functionality to be implemented');
  };

  useEffect(() => {
    if (Object.keys(filters).length > 2) { // More than just limit and offset
      handleSearch();
    }
  }, [filters]);

  const totalPages = Math.ceil(totalResults / (filters.limit || 25));

  return (
    <AppLayout currentPath="/search">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Search Licenses</h1>
            <p className="mt-1 text-sm text-gray-500">
              Find businesses by name, license type, location, and more
            </p>
          </div>
          <div className="flex items-center space-x-3">
            <Button
              variant="outline"
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center"
            >
              <FunnelIcon className="h-4 w-4 mr-2" />
              {showFilters ? 'Hide' : 'Show'} Filters
            </Button>
            {results.length > 0 && (
              <>
                <Button variant="outline" onClick={saveQuery}>
                  <BookmarkIcon className="h-4 w-4 mr-2" />
                  Save Query
                </Button>
                <Button variant="outline" onClick={exportResults}>
                  <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
                  Export CSV
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Filters */}
        {showFilters && (
          <div className="bg-white shadow rounded-lg p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <Input
                label="Business Name"
                placeholder="Search by business name..."
                value={filters.business_name || ''}
                onChange={(e) => handleFilterChange('business_name', e.target.value)}
              />
              
              <Select
                label="License Type"
                placeholder="Select license type"
                value={filters.license_types?.[0] || ''}
                onChange={(value) => handleFilterChange('license_types', value ? [value as string] : undefined)}
                options={LICENSE_TYPES}
              />
              
              <Select
                label="License Status"
                placeholder="Select status"
                value={filters.license_type_status?.[0] || ''}
                onChange={(value) => handleFilterChange('license_type_status', value ? [value as string] : undefined)}
                options={LICENSE_STATUS}
              />
              
              <Input
                label="City"
                placeholder="Enter city name..."
                value={filters.cities?.[0] || ''}
                onChange={(e) => handleFilterChange('cities', e.target.value ? [e.target.value] : undefined)}
              />
              
              <Input
                label="ZIP Code"
                placeholder="Enter ZIP code..."
                value={filters.zip_codes?.[0] || ''}
                onChange={(e) => handleFilterChange('zip_codes', e.target.value ? [e.target.value] : undefined)}
              />
              
              <div className="grid grid-cols-2 gap-2">
                <Input
                  label="Min Rating"
                  type="number"
                  min="0"
                  max="5"
                  step="0.1"
                  placeholder="0.0"
                  value={filters.google_rating_min || ''}
                  onChange={(e) => handleFilterChange('google_rating_min', e.target.value ? parseFloat(e.target.value) : undefined)}
                />
                <Input
                  label="Max Rating"
                  type="number"
                  min="0"
                  max="5"
                  step="0.1"
                  placeholder="5.0"
                  value={filters.google_rating_max || ''}
                  onChange={(e) => handleFilterChange('google_rating_max', e.target.value ? parseFloat(e.target.value) : undefined)}
                />
              </div>
            </div>
            
            <div className="mt-4 flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={filters.has_google_data || false}
                    onChange={(e) => handleFilterChange('has_google_data', e.target.checked || undefined)}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">Only show businesses with Google data</span>
                </label>
              </div>
              
              <div className="flex items-center space-x-2">
                <Button variant="outline" onClick={clearFilters}>
                  <XMarkIcon className="h-4 w-4 mr-2" />
                  Clear Filters
                </Button>
                <Button onClick={handleSearch} loading={loading}>
                  <MagnifyingGlassIcon className="h-4 w-4 mr-2" />
                  Search
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Results */}
        <div className="bg-white shadow rounded-lg">
          {error && (
            <div className="p-4 bg-red-50 border-l-4 border-red-400">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}
          
          {totalResults > 0 && (
            <div className="px-6 py-4 border-b border-gray-200">
              <p className="text-sm text-gray-700">
                Found <span className="font-medium">{totalResults.toLocaleString()}</span> results
              </p>
            </div>
          )}
          
          <Table
            data={results}
            columns={columns}
            loading={loading}
            emptyMessage="No businesses found. Try adjusting your search criteria."
          />
          
          {totalPages > 1 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={totalResults}
              itemsPerPage={filters.limit || 25}
              onPageChange={handlePageChange}
            />
          )}
        </div>
      </div>
    </AppLayout>
  );
};
